import { getNewSubscriptionGetCompanyInfo } from '@apis/Customers';
import { CompanyInfo } from '@apis/Customers/model';
import { BillingInvoice, InvoiceProcessingLogEntry } from '@apis/Invoices/model';
import { postJobCompanyQuery } from '@apis/Jobs';
import styled from '@emotion/styled';
import { ActionIcon, Box, Divider, Loader, Popover, Title, Text, Tabs, Group, useMantineTheme } from '@mantine/core';
import { InvoiceDetailsForm } from '@root/Components/BillingInvoices/InvoiceDetailsForm';
import { useCompany } from '@root/Components/Router/CompanyContent';
import { SettingsWithPreviewContainer } from '@root/Design/Settings';
import { SidePanelContentEl, SidePanelPanelEl, SidePanelTitleEl, SidePanelXButtonEl } from '@root/Design/SidePanel';
import { request } from '@root/Services/BasicApi';
import { UserPreferenceService } from '@root/Services/Customers/UserPreferenceService';
import { useDi, useDiMemo } from '@root/Services/DI';
import { FormatService, NamedFormats } from '@root/Services/FormatService';
import { queryBuilder } from '@root/Services/QueryExpr';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Check, CircleCaretRight, CircleCheck, CircleDot, Clock, StatusChange, X } from 'tabler-icons-react';
import { BillingReconciliationProvider, ReconciliationGrid } from '../Showback/Reconciliation/ReconciliationGrid';
import { getBillingInvoiceGetInvoiceProcessingLogEntries } from '@apis/Invoices';
import { theme } from '@root/Design/Themes';
import { CompanyContextService } from '@root/Services/Customers/CompanyContext';

// #region Processing Status Types & Helpers
type JobType = { Id: string; Type: string; QueuedAt: string; Parameters: { Month: number; Year: number } };

type StepStatus = 'not-started' | 'in-progress' | 'succeeded' | 'failed';

interface ProcessingStepInfo {
    label: string;
    key: string;
    status: StepStatus;
    startedAt?: string | null;
    finishedAt?: string | null;
    failureDetails?: string | null;
}

const STEP_DEFINITIONS: { label: string; key: string; stepEnum: string; rollupType?: string }[] = [
    { label: '1 - Staged', key: 'staged', stepEnum: 'StageCurFiles' },
    { label: '2 - Processing', key: 'processing', stepEnum: 'ProcessCurFiles' },
    { label: '3-A Invoice Explorer', key: 'invoice-explorer', stepEnum: 'IndexProcessedFiles', rollupType: 'BasicDailyRollupIndexer' },
    { label: '3-B Trend Analysis', key: 'trend-analysis', stepEnum: 'IndexProcessedFiles', rollupType: 'BasicMonthlyRollupIndexer' },
    { label: '3-C Resource Cost', key: 'resource-cost', stepEnum: 'IndexProcessedFiles', rollupType: 'DailySpendByResourceId' },
    {
        label: '3-D Billing Reconciliation',
        key: 'billing-reconciliation',
        stepEnum: 'IndexProcessedFiles',
        rollupType: 'BillingReconciliationRollupIndexer',
    },
];

function getEntryStatus(entry?: InvoiceProcessingLogEntry | null): StepStatus {
    if (!entry) return 'not-started';
    if (entry.FailedAt) return 'failed';
    if (entry.SucceededAt) return 'succeeded';
    return 'in-progress';
}

function buildSteps(entries: InvoiceProcessingLogEntry[]): ProcessingStepInfo[] {
    return STEP_DEFINITIONS.map((def) => {
        const entry = entries.find((e) => {
            const stepMatch = e.Step === def.stepEnum;
            if (!stepMatch) return false;
            if (def.rollupType) return e.RollupType === def.rollupType;
            // For non-indexing steps (Staged, Processing), match entries without a rollupType or any
            return def.stepEnum !== 'IndexProcessedFiles';
        });
        const status = getEntryStatus(entry);
        return {
            label: def.label,
            key: def.key,
            status,
            startedAt: entry?.StartedAt,
            finishedAt: entry?.SucceededAt ?? entry?.FailedAt,
            failureDetails: entry?.FailureDetails,
        };
    });
}

function getOverallStatus(steps: ProcessingStepInfo[]): StepStatus {
    if (steps.some((s) => s.status === 'failed')) return 'failed';
    if (steps.some((s) => s.status === 'in-progress')) return 'in-progress';
    if (steps.every((s) => s.status === 'succeeded')) return 'succeeded';
    return 'not-started';
}

function useProcessingStatus(invoice: BillingInvoice, reprocessTrigger: number) {
    const [logEntries, setLogEntries] = useState<InvoiceProcessingLogEntry[]>([]);
    const [processingJobId, setProcessingJobId] = useState<string | undefined>();
    const [loading, setLoading] = useState(true);
    const jobIdRef = useRef<string | undefined>();
    const companyCtx = useDi(CompanyContextService);

    const fetchLogEntries = useCallback(async () => {
        const jobId = jobIdRef.current;
        if (!jobId || !invoice.CompanyId) return;
        try {
            const entries = await getBillingInvoiceGetInvoiceProcessingLogEntries(
                { companyId: invoice.CompanyId, jobId },
                companyCtx.getRequestOptions(true)
            );
            setLogEntries(entries ?? []);
        } catch {}
    }, [invoice.CompanyId]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                // 1. Query the jobs API to find the most recent processing job for this month/year
                const mostRecentProcessingJob = await queryBuilder<JobType>()
                    .where((b) =>
                        b.model.Type.eq([
                            'Cloudsaver.InvoiceIngestion.Application.Subscriptions.CurFilesStaged',
                            'Cloudsaver.InvoiceIngestion.Application.Subscriptions.ExportFilesStaged',
                        ])
                    )
                    .sortDesc((b) => b.model.QueuedAt)
                    .execute(postJobCompanyQuery)
                    .then((r) => r.Results ?? [])
                    .then((r) => r.find((v) => v.Parameters.Year === invoice.Year && v.Parameters.Month === invoice.Month));

                if (cancelled) return;
                const jobId = mostRecentProcessingJob?.Id;
                setProcessingJobId(jobId);
                jobIdRef.current = jobId;

                if (!jobId || !invoice.CompanyId) {
                    setLoading(false);
                    return;
                }

                // 2. Get invoice processing log entries for this job
                fetchLogEntries();
            } catch {
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [invoice.Year, invoice.Month, invoice.CompanyId]);

    const steps = useMemo(() => buildSteps(logEntries), [logEntries]);
    const overallStatus = useMemo(() => getOverallStatus(steps), [steps]);

    // Poll every 10 seconds while any step is in-progress
    useEffect(() => {
        if (overallStatus !== 'in-progress') return;
        const interval = setInterval(fetchLogEntries, 10000);
        return () => clearInterval(interval);
    }, [overallStatus, fetchLogEntries]);

    // After reprocess is clicked, poll jobs API every 10s until a new job appears
    useEffect(() => {
        if (reprocessTrigger === 0) return;
        const previousJobId = jobIdRef.current;
        let cancelled = false;

        const pollForNewJob = async () => {
            try {
                const job = await queryBuilder<JobType>()
                    .where((b) =>
                        b.model.Type.eq([
                            'Cloudsaver.InvoiceIngestion.Application.Subscriptions.CurFilesStaged',
                            'Cloudsaver.InvoiceIngestion.Application.Subscriptions.ExportFilesStaged',
                        ])
                    )
                    .sortDesc((b) => b.model.QueuedAt)
                    .execute(postJobCompanyQuery)
                    .then((r) => r.Results ?? [])
                    .then((r) => r.find((v) => v.Parameters.Year === invoice.Year && v.Parameters.Month === invoice.Month));

                if (cancelled) return;
                const newJobId = job?.Id;
                if (newJobId && newJobId !== previousJobId) {
                    setProcessingJobId(newJobId);
                    jobIdRef.current = newJobId;
                    setLogEntries([]);
                    setLoading(true);
                    const entries = await getBillingInvoiceGetInvoiceProcessingLogEntries({
                        companyId: invoice.CompanyId!,
                        jobId: newJobId,
                    });
                    if (!cancelled) {
                        setLogEntries(entries ?? []);
                        setLoading(false);
                    }
                    return true;
                }
            } catch {}
            return false;
        };

        const interval = setInterval(async () => {
            const found = await pollForNewJob();
            if (found) clearInterval(interval);
        }, 10000);

        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [reprocessTrigger, invoice.Year, invoice.Month, invoice.CompanyId]);

    return { steps, overallStatus, loading, processingJobId };
}
// #endregion

// #region Processing Status Icon & Popover
const StatusIcon = ({ status, size = 16 }: { status: StepStatus; size?: number }) => {
    switch (status) {
        case 'succeeded':
            return <Check size={size} color="success" />;
        case 'failed':
            return <X size={size} color="error" />;
        case 'in-progress':
            return <Loader size={size} color="primary" />;
        default:
            return <CircleDot size={size} color="gray" />;
    }
};

const OverallStatusIcon = ({ status }: { status: StepStatus }) => {
    switch (status) {
        case 'succeeded':
            return <CircleCheck size={18} color="success" />;
        case 'failed':
            return <AlertCircle size={18} color="error" />;
        case 'in-progress':
            return <CircleCaretRight size={18} color="primary" />;
        default:
            return <Clock size={18} color="gray" />;
    }
};

function ProcessingStatusButton({ invoice, fmtSvc, reprocessTrigger }: { invoice: BillingInvoice; fmtSvc: FormatService; reprocessTrigger: number }) {
    const { steps, overallStatus, loading } = useProcessingStatus(invoice, reprocessTrigger);
    const [opened, setOpened] = useState(false);

    if (loading) {
        return <Loader size={18} />;
    }

    return (
        <Popover width={425} opened={opened} onChange={setOpened} position="bottom-end" withArrow shadow="md">
            <Popover.Target>
                <ActionIcon variant="subtle" onClick={() => setOpened((o) => !o)} title="Processing Status">
                    <OverallStatusIcon status={overallStatus} />
                </ActionIcon>
            </Popover.Target>
            <Popover.Dropdown>
                <Box>
                    <Text weight={600} size="sm" mb="xs">
                        Invoice Processing Status
                    </Text>
                    <Divider mb="xs" />
                    {steps.map((step) => (
                        <StepRow key={step.key}>
                            <Group spacing="xs" noWrap>
                                <StatusIcon status={step.status} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Text size="sm" weight={500}>
                                        {step.label}
                                    </Text>
                                    <Group spacing={4}>
                                        {step.startedAt && (
                                            <Text size="xs" color="dimmed">
                                                Started: {fmtSvc.toLocalNoSeconds(step.startedAt)}
                                            </Text>
                                        )}
                                        {step.finishedAt && (
                                            <Text size="xs" color="dimmed">
                                                {' | '}
                                                {step.status === 'failed' ? 'Failed' : 'Completed'}: {fmtSvc.toLocalNoSeconds(step.finishedAt)}
                                            </Text>
                                        )}
                                    </Group>
                                    {step.status === 'failed' && step.failureDetails && (
                                        <Text size="xs" color="error" lineClamp={2} title={step.failureDetails}>
                                            {step.failureDetails}
                                        </Text>
                                    )}
                                </Box>
                            </Group>
                        </StepRow>
                    ))}
                </Box>
            </Popover.Dropdown>
        </Popover>
    );
}
// #endregion

export interface BillingInvoiceSidePanelProps {
    toggleSidePanel: (showSidePanel: boolean) => void;
    fmtSvc: FormatService;
    billingInvoice: BillingInvoice;
    onClose: (didEdit: boolean) => void;
    onPreviewCollapsedChange?: (collapsed: boolean) => void;
    title?: string;
    subtitle?: string;
    settingsWidth: number;
}

export const BillingInvoiceSidePanel = (props: BillingInvoiceSidePanelProps) => {
    const [compInfo, setCompInfo] = useState<CompanyInfo>({} as CompanyInfo);
    const fmtSvc = useDi(FormatService);
    const [activeTab, setActiveTab] = useState<string | null>('invoice');
    const [adjustmentCopied, setAdjustmentCopied] = useState(false);
    const [didSave, setDidSave] = useState(false);
    const [reprocessTrigger, setReprocessTrigger] = useState(0);
    const theme = useMantineTheme();

    const handleTabChange = (value: string) => {
        setActiveTab(value);
    };

    const company = useCompany();
    useEffect(() => {
        loadCompanyInfo();
    }, []);

    const loadCompanyInfo = () => {
        if (company) {
            getNewSubscriptionGetCompanyInfo()
                .then((info) => {
                    setCompInfo(info);
                })
                .catch((e) => {});
        }
    };

    const close = (didEdit: boolean) => {
        props.onClose(didEdit || adjustmentCopied || didSave);
    };

    return (
        <SettingsWithPreviewContainer
            title="Reconciliation"
            preview={<BillingInvoiceLiveContent billingInvoice={props.billingInvoice} />}
            previewTransparent={false}
            previewScrollable
            previewType="stack"
            collapsible
            defaultCollapsed
            onCollapsedChange={props.onPreviewCollapsedChange}
            settingsWidth={props.settingsWidth}
            settings={(collapseButton: ReactNode | null) => (
                <SidePanelPanelEl>
                    <Group position="apart" px="lg" py="md" sx={{ borderBottom: `solid 1px ${theme.colors.gray[3]}` }}>
                        <SidePanelTitleEl>
                            <Box>
                                <Title order={3}>{props.title ? props.title : compInfo.CompanyName}</Title>
                                <Text size="sm">
                                    {props.fmtSvc.userFriendlyCamelCase(
                                        props.subtitle
                                            ? props.subtitle
                                            : fmtSvc.formatShortMonthYear(new Date(props.billingInvoice.Year!, props.billingInvoice.Month! - 1))
                                    )}
                                </Text>
                            </Box>
                        </SidePanelTitleEl>
                        <Group spacing="xs">
                            <ProcessingStatusButton invoice={props.billingInvoice} fmtSvc={fmtSvc} reprocessTrigger={reprocessTrigger} />
                            {collapseButton}
                            <SidePanelXButtonEl onClick={() => close(false)} />
                        </Group>
                    </Group>
                    <SidePanelContentEl padding={0}>
                        <Tabs
                            defaultValue="invoice"
                            value={activeTab}
                            onTabChange={handleTabChange}
                            sx={{ flex: 1, display: 'flex', height: '100%', flexDirection: 'column', minHeight: 0 }}
                        >
                            <Tabs.List px="md">
                                <Tabs.Tab value="invoice">Invoice</Tabs.Tab>
                                <Tabs.Tab value="fees">Fees</Tabs.Tab>
                                <Tabs.Tab value="margin">Margin</Tabs.Tab>
                            </Tabs.List>
                            <PanelContainer>
                                <Tabs.Panel value="invoice">
                                    <InvoiceDetailsForm
                                        onClose={close}
                                        onCopy={() => setAdjustmentCopied(true)}
                                        onSave={() => setDidSave(true)}
                                        billingInvoice={props.billingInvoice}
                                        onReprocess={() => setReprocessTrigger((t) => t + 1)}
                                    ></InvoiceDetailsForm>
                                </Tabs.Panel>
                                <Tabs.Panel value="fees">
                                    <Box p="lg">
                                        <Title order={3}>Fees</Title>
                                        <Divider />
                                        <Text>Fees data goes here</Text>
                                    </Box>
                                </Tabs.Panel>
                                <Tabs.Panel value="margin">
                                    <Box p="lg">
                                        <Title order={3}>Margin</Title>
                                        <Divider />
                                        <Text>Margin data goes here</Text>
                                    </Box>
                                </Tabs.Panel>
                            </PanelContainer>
                        </Tabs>
                    </SidePanelContentEl>
                </SidePanelPanelEl>
            )}
        />
    );
};

function BillingInvoiceLiveContent({ billingInvoice }: { billingInvoice: BillingInvoice }) {
    const userPrefSvc = useDiMemo(UserPreferenceService);
    const moneyFmt = (userPrefSvc.get('ShowbackUserPrefs', { format: 'number-with-two-decimals' })?.format ??
        'number-with-two-decimals') as NamedFormats;

    if (!billingInvoice?.Year || !billingInvoice?.Month || !billingInvoice?.CompanyId) {
        return null;
    }

    const month = new Date(billingInvoice.Year!, billingInvoice.Month! - 1);
    const provider = new BillingReconciliationProvider(billingInvoice.CompanyId);

    return (
        <LiveContentContainer>
            <ReconciliationGrid month={month} moneyFormat={moneyFmt} provider={provider} />
        </LiveContentContainer>
    );
}

const PanelContainer = styled.div`
    height: 100%;
    flex: 1;
    min-height: 0;
    > div {
        height: 100%;
        overflow: auto;
    }
`;

const LiveContentContainer = styled.div`
    height: 100%;
    flex: 1;
    display: flex;
    flex-direction: column;
`;

const StepRow = styled.div`
    padding: 6px 0;
    &:not(:last-child) {
        border-bottom: solid 1px #e9ecef;
    }
`;
